import { getDocumentRuntime } from '../../../backend/runtime';
import React, { useEffect, useRef, useState } from 'react';
import { listReprocessing, findReprocessingSources, createReprocessing, actOnReprocessing } from '../../../service/reprocessing';

const stages = {
  allocated: 'Awaiting return', return_transit: 'Returning to Standard', repair: 'At Standard for repair',
  repaired: 'Repaired, ready to ship', reship_transit: 'Returning to ESUN', accepted: 'Accepted, ready to pack',
  rejected: 'Rejected after repair', lost: 'Unsalvageable', packed: 'Packed',
};
const validQuantity = (value, maximum) => Number.isFinite(Number(value)) && Number(value) > 0 && Number(value) <= maximum;

export default function Reprocessing() {
  const [data, setData] = useState({ records: [], role: '', has_more: false });
  const [search, setSearch] = useState('');
  const [sources, setSources] = useState([]);
  const [sourceId, setSourceId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [selection, setSelection] = useState(null);
  const [actionQty, setActionQty] = useState('');
  const [note, setNote] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const inFlight = useRef(false);
  const storageKey = useRef(getDocumentRuntime().storageKey('reprocessing-pending'));
  const pending = useRef(undefined);
  if (pending.current === undefined) {
    try { pending.current = JSON.parse(sessionStorage.getItem(storageKey.current)) || null; }
    catch { pending.current = null; }
  }
  const savePending = (value) => {
    if (value) sessionStorage.setItem(storageKey.current, JSON.stringify(value));
    else sessionStorage.removeItem(storageKey.current);
    pending.current = value;
  };
  const source = sources.find((item) => String(item.id) === sourceId);

  // Retain the exact key for retries after a lost response. Changing the payload is
  // blocked until success so an uncertain submission cannot become a duplicate.
  const submit = async (kind, payload) => {
    if (inFlight.current) return;
    const signature = JSON.stringify({ kind, payload });
    if (pending.current && pending.current.signature !== signature) {
      setMessage('Retry the pending submission with the same values before starting another operation.');
      return;
    }
    const attempt = pending.current || { signature, kind, payload, key: crypto.randomUUID() };
    savePending(attempt);
    inFlight.current = true;
    setBusy(true);
    try {
      const response = await (kind === 'create' ? createReprocessing : actOnReprocessing)({ ...payload, request_id: attempt.key });
      if (!response) {
        setMessage('Submission was not confirmed. Keep these values and retry; the same request will not be recorded twice.');
        return;
      }
      savePending(null);
      if (response.data.rejected) { setMessage(response.message); return; }
      if (kind === 'create') setSources((old) => old.map((item) => item.id === payload.scrap_id ? { ...item, remaining: item.remaining - payload.quantity } : item).filter((item) => item.remaining > 0));
      setData((old) => ({ ...old, records: [response.data, ...old.records.filter((r) => r.id !== response.data.id)] }));
      setSelection(null);
      setQuantity('');
      setSourceId('');
      setReference('');
      setMessage(response.message);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  const load = async (page = 1, term = search) => {
    if (inFlight.current) return;
    setBusy(true);
    try {
      const response = await listReprocessing({ search: term, page });
      if (response) {
        setData((old) => ({ ...response.data, records: page === 1 ? response.data.records : [...old.records, ...response.data.records], page }));
        setLoaded(true);
      }
    } finally { setBusy(false); }
  };
  useEffect(() => {
    let active = true;
    listReprocessing().then((response) => {
      if (active && response) { setData({ ...response.data, page: 1 }); setLoaded(true); }
      else if (active) setUnavailable(true);
    });
    return () => { active = false; };
  }, []);

  const findSources = async () => {
    setBusy(true);
    try {
      const response = await findReprocessingSources({ search });
      if (response) { setSources(response.data.records); setSourceId(''); }
    } finally { setBusy(false); }
  };

  return (
    <main className="container-fluid p-4">
      <h1 className="h4">Damage returns &amp; reprocessing</h1>
      <p>Return existing damaged garments for repair. Standard reprocessing is no charge. Pack accepted finished goods here without repeating manufacturing.</p>
      <p className="text-muted">Use the original receiving screen to report damage first. Only completed, damage-linked finished-product scraps are eligible. Items needing reconciliation or unfinished production must be reviewed before starting.</p>
      {message && <p role="status" className="alert alert-info">{message}</p>}
      {pending.current && <div className="alert alert-warning" role="status">
        A submission is awaiting confirmation. Retry safely using its original request identifier.
        <button className="btn btn-outline-primary ms-2" disabled={busy} onClick={() => submit(pending.current.kind, pending.current.payload)}>Retry pending submission</button>
      </div>}
      {!loaded && <p>{unavailable ? 'Reprocessing is unavailable for this vendor. Check the connection and company reprocessing setup.' : 'Loading reprocessing access…'}</p>}
      <div className="d-flex gap-2 mb-3">
        <input aria-label="Lot or reference search" className="form-control" value={search} onChange={(e) => setSearch(e.target.value)} maxLength={100} />
        <button className="btn btn-outline-primary" disabled={busy} onClick={() => load()}>Search cases</button>
        {data.role === 'receiver' && <button className="btn btn-outline-primary" disabled={busy || !search.trim() || !!pending.current} onClick={findSources}>Find damaged lot</button>}
      </div>
      {data.role === 'receiver' && sources.length > 0 && (
        <form className="card p-3 mb-4" onSubmit={(e) => { e.preventDefault(); submit('create', { scrap_id: source.id, quantity: Number(quantity), reference }); }}>
          <h2 className="h5">Open a damaged return</h2>
          <label>Source damage<select aria-label="Source damage" className="form-select" value={sourceId} disabled={busy || !!pending.current} onChange={(e) => setSourceId(e.target.value)}>
            <option value="">Select the original product and lot</option>
            {sources.map((s) => <option key={s.id} value={s.id}>{s.lot} · {s.product} · {s.name} · {s.remaining} {s.uom || 'units'} available</option>)}
          </select></label>
          {source && <p>Original receipt: {source.source} · Damage: {source.damage}</p>}
          <label>Return quantity<input aria-label="Return quantity" className="form-control" type="number" min="1" max={source?.remaining} value={quantity} disabled={busy || !!pending.current} onChange={(e) => setQuantity(e.target.value)} /></label>
          <label>Packing list and damage reason<input aria-label="Packing list and damage reason" className="form-control" maxLength={500} value={reference} disabled={busy || !!pending.current} onChange={(e) => setReference(e.target.value)} /></label>
          <button className="btn btn-primary mt-2" disabled={busy || !source || !validQuantity(quantity, source.remaining) || !reference.trim()}>Open return — no charge</button>
        </form>
      )}
      {loaded && !data.records.length && <p>No reprocessing cases match this search.</p>}
      {data.records.map((record) => (
        <section className="card p-3 mb-3" key={record.id}>
          <h2 className="h5">{record.name} · {record.lot} <span className="badge bg-success">No charge</span></h2>
          <p>{record.product} · {record.quantity} {record.uom || 'units'} · {record.reference}</p>
          <p>Original receipt: {record.source} · Damage: {record.damage}</p>
          <dl className="d-flex flex-wrap gap-3">{Object.entries(record.balances).filter(([, value]) => value > 0).map(([key, value]) => <div key={key}><dt>{stages[key]}</dt><dd>{value}</dd></div>)}</dl>
          <div className="d-flex flex-wrap gap-2">{record.actions.map((action) => <button key={action.key} className="btn btn-outline-primary" disabled={busy || !!pending.current} onClick={() => { setSelection({ record, action }); setActionQty(''); setNote(''); }}>{action.label}</button>)}</div>
          {selection?.record.id === record.id && (
            <form className="border rounded p-3 mt-3" onSubmit={(e) => { e.preventDefault(); submit('action', { case_id: record.id, action: selection.action.key, quantity: Number(actionQty), note }); }}>
              <h3 className="h6">{selection.action.label} · {selection.action.maximum} {record.uom || 'units'} available</h3>
              <label>Quantity<input aria-label="Action quantity" className="form-control" type="number" min="1" max={selection.action.maximum} value={actionQty} disabled={busy || !!pending.current} onChange={(e) => setActionQty(e.target.value)} /></label>
              <label>Shipment / repair / inspection reference<input aria-label="Action reference" className="form-control" value={note} maxLength={500} disabled={busy || !!pending.current} onChange={(e) => setNote(e.target.value)} /></label>
              <button className="btn btn-primary mt-2" disabled={busy || !validQuantity(actionQty, selection.action.maximum) || !note.trim()}>Record {selection.action.label.toLowerCase()}</button>
              <button type="button" className="btn btn-link" disabled={busy || !!pending.current} onClick={() => setSelection(null)}>Cancel</button>
            </form>
          )}
          <details className="mt-3"><summary>History and packages ({record.events.length})</summary>
            <table className="table"><thead><tr><th>Recorded</th><th>Action</th><th>Quantity</th><th>Reference</th><th>Transfer</th><th>Package</th></tr></thead>
              <tbody>{record.events.map((event, i) => <tr key={i}><td>{event.date}</td><td>{event.action}</td><td>{event.quantity}</td><td>{event.note}</td><td>{event.transfer}</td><td>{event.package || '—'}</td></tr>)}</tbody>
            </table>
          </details>
        </section>
      ))}
      {data.has_more && <button className="btn btn-outline-primary" disabled={busy} onClick={() => load((data.page || 1) + 1)}>Load more cases</button>}
    </main>
  );
}
